"use client";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/client/api/axios";
import { useAuthStore, type AuthState } from "@/store/authStore";

export type CartStatus = "active" | "converted" | "abandoned";
export type CartBookingType = "BOOK_NOW" | "SCHEDULE" | "REPEAT";

export interface CartItem {
  id: number;
  cartId: number;
  cityServiceId: number;
  itemName: string;
  quantity: number;
  itemPrice: number;
  createdAt?: string;
}

export interface Cart {
  id: number;
  userId: number;
  cityId: number;
  cityName?: string;
  bookingType: CartBookingType;
  serviceType: string;
  providerName: string | null;
  status: CartStatus;
  cartTotal: number;
  cartQty: number;
  createdAt: string;
  updatedAt?: string;
  items: CartItem[];
}

const CARTS_QUERY_KEY = ["carts"] as const;

export interface CartsQueryParams {
  status?: CartStatus;
  city_id?: number;
}

async function fetchCarts(params?: CartsQueryParams) {
  const { data } = await api.get<{ success: boolean; data: Cart[] }>("/carts", {
    params: params ?? {},
  });
  return data.data ?? data;
}

async function fetchCart(id: number) {
  const { data } = await api.get<{ success: boolean; data: Cart }>(`/carts/${id}`);
  return data.data ?? data;
}

async function fetchCartItems(cartId: number) {
  const { data } = await api.get<{ success: boolean; data: CartItem[] }>(
    `/carts/${cartId}/items`
  );
  return data.data ?? data;
}

export interface UseCartsQueryOptions {
  enabled?: boolean;
}

export function useCartsQuery(
  params?: CartsQueryParams,
  options?: UseCartsQueryOptions
) {
  const user = useAuthStore((s: AuthState) => s.user);
  const isCartUser =
    !!user &&
    (user.role === "customer" || user.role === "property_manager");
  return useQuery({
    queryKey: [...CARTS_QUERY_KEY, params ?? {}],
    queryFn: () => fetchCarts(params),
    enabled: (options?.enabled !== false) && isCartUser,
  });
}

export function useCartQuery(cartId: number | null) {
  return useQuery({
    queryKey: [...CARTS_QUERY_KEY, cartId],
    queryFn: () => fetchCart(cartId!),
    enabled: cartId != null && cartId > 0,
  });
}

export function useCartItemsQuery(cartId: number | null) {
  return useQuery({
    queryKey: [...CARTS_QUERY_KEY, cartId, "items"],
    queryFn: () => fetchCartItems(cartId!),
    enabled: cartId != null && cartId > 0,
  });
}

export interface CreateCartPayload {
  city_id: number;
  booking_type?: CartBookingType;
  service_type?: string;
  provider_name?: string | null;
}

async function createCart(payload: CreateCartPayload) {
  const { data } = await api.post<{ success: boolean; data: Cart }>(
    "/carts",
    payload
  );
  return data.data ?? data;
}

export function useAddCartMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCart,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CARTS_QUERY_KEY });
    },
  });
}

export interface UpdateCartPayload {
  status?: CartStatus;
  provider_name?: string | null;
}

async function updateCart(id: number, payload: UpdateCartPayload) {
  const { data } = await api.patch<{ success: boolean; data: Cart }>(
    `/carts/${id}`,
    payload
  );
  return data.data ?? data;
}

export function useUpdateCartMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateCartPayload }) =>
      updateCart(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: CARTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: [...CARTS_QUERY_KEY, id] });
    },
  });
}

async function deleteCart(id: number) {
  await api.delete(`/carts/${id}`);
}

export function useDeleteCartMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCart,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CARTS_QUERY_KEY });
    },
  });
}

export interface AddCartItemPayload {
  city_service_id: number;
  quantity?: number;
  vendor_id?: number | null;
}

async function addCartItem(cartId: number, payload: AddCartItemPayload) {
  const { data } = await api.post<{ success: boolean; data: CartItem }>(
    `/carts/${cartId}/items`,
    payload
  );
  return data.data ?? data;
}

export function useAddCartItemMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      cartId,
      payload,
    }: {
      cartId: number;
      payload: AddCartItemPayload;
    }) => addCartItem(cartId, payload),
    onSuccess: (_, { cartId }) => {
      queryClient.invalidateQueries({ queryKey: CARTS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [...CARTS_QUERY_KEY, cartId],
      });
      queryClient.invalidateQueries({
        queryKey: [...CARTS_QUERY_KEY, cartId, "items"],
      });
    },
  });
}

export interface UpdateCartItemPayload {
  item_id: number;
  quantity: number;
}

async function updateCartItem(
  cartId: number,
  payload: UpdateCartItemPayload
) {
  const { data } = await api.patch<{ success: boolean; data: CartItem[] }>(
    `/carts/${cartId}/items`,
    payload
  );
  return data.data ?? data;
}

export function useUpdateCartItemMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      cartId,
      payload,
    }: {
      cartId: number;
      payload: UpdateCartItemPayload;
    }) => updateCartItem(cartId, payload),
    onSuccess: (_, { cartId }) => {
      queryClient.invalidateQueries({ queryKey: CARTS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [...CARTS_QUERY_KEY, cartId],
      });
      queryClient.invalidateQueries({
        queryKey: [...CARTS_QUERY_KEY, cartId, "items"],
      });
    },
  });
}

async function removeCartItem(cartId: number, itemId: number) {
  await api.delete(`/carts/${cartId}/items`, {
    params: { item_id: itemId },
  });
}

export function useRemoveCartItemMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cartId, itemId }: { cartId: number; itemId: number }) =>
      removeCartItem(cartId, itemId),
    onSuccess: (_, { cartId }) => {
      queryClient.invalidateQueries({ queryKey: CARTS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [...CARTS_QUERY_KEY, cartId],
      });
      queryClient.invalidateQueries({
        queryKey: [...CARTS_QUERY_KEY, cartId, "items"],
      });
    },
  });
}
